import { useCallback, useState } from 'react';
import { formatDateShort, formatDateWithWeekday } from '../../utils/dateHelpers';
import { JourneyPickerWrapper } from './JourneyPicker.styles';
import { JourneyPickerDesktop } from './JourneyPickerDesktop';
import { JourneyPickerMobile } from './JourneyPickerMobile';
import type { JourneyPickerProps } from './JourneyPicker.types';
import type { JourneyPickerSharedProps } from './JourneyPickerView.types';

function buildDatesLabel(
  fromDate: string,
  toDate: string,
  singleMode: boolean,
  selectDate: string,
  selectDates: string
): string {
  if (singleMode) {
    if (!fromDate) return selectDate;
    return formatDateWithWeekday(fromDate);
  }
  if (!fromDate && !toDate) return selectDates;
  if (fromDate && !toDate) return formatDateShort(fromDate);

  const fromDay = fromDate.split('-')[2].replace(/^0/, '');
  const to = formatDateShort(toDate);

  const fromMonth = fromDate.slice(5, 7);
  const toMonth = toDate.slice(5, 7);
  if (fromMonth === toMonth && fromDate.slice(0, 4) === toDate.slice(0, 4)) {
    return `${fromDay} - ${to}`;
  }

  return `${formatDateShort(fromDate)} - ${to}`;
}

export function JourneyPicker({
  category,
  categories,
  onCategoryChange,
  disableCategorySelect,
  dateMode = 'range',
  showGuests,
  guestMode = 'split',
  defaultFromDate = '',
  defaultToDate = '',
  defaultAdults = 2,
  defaultChildren = 0,
  availability,
  isLoadingAvailability,
  onMonthChange,
  columnView,
  numberOfMonths,
  onSearch,
  labels: l,
}: JourneyPickerProps) {
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(defaultToDate);
  const [adults, setAdults] = useState(defaultAdults);
  const [children, setChildren] = useState(defaultChildren);

  const isSingleDate = dateMode === 'single';
  const displayGuests = showGuests !== undefined ? showGuests : !isSingleDate;

  const handleRangeSelect = useCallback(
    (checkIn: string, checkOut: string) => {
      setFromDate(checkIn);
      setToDate(isSingleDate ? '' : checkOut);
    },
    [isSingleDate]
  );

  const handleAdultsChange = useCallback((val: number) => setAdults(val), []);
  const handleChildrenChange = useCallback((val: number) => setChildren(val), []);

  const handleCategorySelect = useCallback(
    (id: string) => {
      onCategoryChange?.(id);
    },
    [onCategoryChange]
  );

  const handleSearch = useCallback(() => {
    if (isSingleDate) {
      if (fromDate) onSearch({ fromDate, adults, children });
    } else {
      if (fromDate && toDate) onSearch({ fromDate, toDate, adults, children });
    }
  }, [isSingleDate, fromDate, toDate, adults, children, onSearch]);

  const selectDateLabel = l?.selectDate ?? 'Select date';
  const selectDatesLabel = l?.selectDates ?? 'Select dates';
  const searchLabel = l?.search ?? 'Search';
  const browseLabel = l?.browse ?? 'Browse';
  const closeLabel = l?.close ?? 'Close';
  const backLabel = l?.back ?? 'Back';
  const editLabel = l?.edit ?? 'Edit';
  const headingLabel = l?.heading ?? 'Change search';
  const updateLabel = l?.update ?? 'Update';
  const changeDateLabel = l?.changeDate ?? 'Change date';
  const changeGuestsLabel = l?.changeGuests ?? 'Guests';
  const guestsAriaLabel = l?.guestPicker?.guests ?? 'Guests';
  const defaultGuestsLabel = (total: number) => `${total} Guest${total !== 1 ? 's' : ''}`;

  const datesLabel = buildDatesLabel(
    fromDate,
    toDate,
    isSingleDate,
    selectDateLabel,
    selectDatesLabel
  );
  const guestsLabel = (l?.guests ?? defaultGuestsLabel)(adults + children);

  const showCategory = Boolean(!disableCategorySelect && categories && categories.length > 0);
  const currentCategoryLabel = categories?.find((c) => c.id === category)?.label ?? browseLabel;

  const triggerTitle = showCategory ? currentCategoryLabel : browseLabel;
  const triggerSubtitle = displayGuests ? `${datesLabel} · ${guestsLabel}` : datesLabel;

  const shared: JourneyPickerSharedProps = {
    showCategory,
    categories,
    category,
    currentCategoryLabel,
    onCategorySelect: handleCategorySelect,
    fromDate,
    toDate,
    isSingleDate,
    datesLabel,
    onRangeSelect: handleRangeSelect,
    displayGuests,
    guestMode,
    adults,
    children,
    guestsLabel,
    onAdultsChange: handleAdultsChange,
    onChildrenChange: handleChildrenChange,
    availability,
    isLoadingAvailability,
    onMonthChange,
    onSearch: handleSearch,
    searchLabel,
    datePickerLabels: l?.datePicker,
    guestPickerLabels: l?.guestPicker,
  };

  return (
    <JourneyPickerWrapper>
      <JourneyPickerMobile
        {...shared}
        triggerTitle={triggerTitle}
        triggerSubtitle={triggerSubtitle}
        closeLabel={closeLabel}
        backLabel={backLabel}
        editLabel={editLabel}
        headingLabel={headingLabel}
        updateLabel={updateLabel}
        changeDateLabel={changeDateLabel}
        changeGuestsLabel={changeGuestsLabel}
        guestsAriaLabel={guestsAriaLabel}
      />

      <JourneyPickerDesktop {...shared} columnView={columnView} numberOfMonths={numberOfMonths} />
    </JourneyPickerWrapper>
  );
}
